import { useEffect, useState } from 'react';
import type { GetAvailableSpotsResponseDto } from '@foodfluence/shared';
import { collabApi } from 'services/api/collab';

/**
 * Remaining spots per campaign id — one request per distinct id, refetched when `refreshKey`
 * changes. Failed lookups are left out, and callers read a missing entry as "not full". Pass
 * `collabId` (single-collab views) so a spot reserved for that collab counts as available.
 * `loading` is true until the first answer for the current ids / collab; later refreshes keep the
 * last counts on screen.
 */
export const useCampaignSpots = (
  campaignIds: string[],
  refreshKey?: string | number,
  collabId?: string,
): { spots: Record<string, GetAvailableSpotsResponseDto>; loading: boolean } => {
  const [spots, setSpots] = useState<Record<string, GetAvailableSpotsResponseDto>>({});
  const idsKey = Array.from(new Set(campaignIds)).sort().join(',');
  const loadKey = `${idsKey}|${collabId ?? ''}`;
  const [loadedKey, setLoadedKey] = useState('');

  useEffect(() => {
    if (!idsKey) return;
    const ids = idsKey.split(',');
    let cancelled = false;
    Promise.allSettled(ids.map((id) => collabApi.getAvailableSpots(id, collabId))).then((results) => {
      if (cancelled) return;
      // Replace, never merge: a failed refetch must read as unknown, not as the last (maybe full) count.
      const next: Record<string, GetAvailableSpotsResponseDto> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[ids[i]] = r.value;
      });
      setSpots(next);
      setLoadedKey(`${idsKey}|${collabId ?? ''}`);
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey, refreshKey, collabId]);

  return { spots, loading: !!idsKey && loadedKey !== loadKey };
};
